// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { setRememberMeToken } from 'utils/local-storage';
import { toggleLanguage } from 'utils/i18n';
import { PRIVATE, ADMIN } from 'router/routes';
import { useSetToken, useUser } from 'utils/contexts/app-context';
import { canManageAccountSettings } from 'utils/accountSettingsPermissions';
import { clearAll } from 'state/ducks/global';
import ImagePlaceHolder from 'components/image-placeholder';
import useOutsideClick from 'hooks/useOutsideClick';
import { Flags } from 'providers/FlagsProvider';
import FeatureFlags from 'utils/feature-flags';
import styles from './styles.module.scss';
import { useTheme } from '@mui/material';
import { useDarkMode } from 'providers/DarkModeProvider';
import { Auth } from 'aws-amplify';

type Props = {
  picture: ?string,
  clearState: () => void,
};

const ProfilePictureWithDropdown = ({
  picture,
  clearState,
}: Props) => {
  const navigate = useNavigate();
  const setToken = useSetToken();
  const user = useUser();
  const [containerRef, didClickOutside] = useOutsideClick();
  const accountRef = React.useRef();
  const [isDropdownVisible, setDropdownVisible] = React.useState(false);
  const { t } = useTranslation();
  const theme = useTheme();
  const { toggleDarkMode } = useDarkMode();

  React.useEffect(() => {
    if (isDropdownVisible && didClickOutside) {
      setDropdownVisible(false);
    }
  }, [isDropdownVisible, didClickOutside, setDropdownVisible]);

  /** Actions */

  const onEditProfile = () => {
    navigate(PRIVATE.profile);
    setDropdownVisible(false);
  };

  const onUsersClick = () => {
    if(user.accessLevel === 'LEAD' ){
      navigate(`/admin/team/${user.team_id}`)
    }else{
      navigate(ADMIN.delegates);
    }
    setDropdownVisible(false);
  };

  const onBilling = () => {
    if (user && user.isAdmin) {
      navigate(ADMIN.plans);
      setDropdownVisible(false);
    }
  };

  const onIntegrations = () => {
    if (user && user.isAdmin) {
      navigate(ADMIN.integrations);
      setDropdownVisible(false);
    }
  };

  const onLogout = async () => {
    try {
      // Sign out from AWS Amplify/Cognito (for Gmail/OAuth logins)
      // This ensures federated sessions are properly cleared
      await Auth.signOut({ global: true });
    } catch (err) {
      // If signOut fails (e.g., user wasn't logged in via Cognito), continue with logout
      // This is expected for regular email/password logins
      console.log('Cognito signOut not needed or failed:', err);
    }
    
    // Clear local tokens and state
    setRememberMeToken(null);
    setToken(null);
    clearState();
    
    // Clear filtering data including labels from localStorage
    // This prevents labels from previous sessions appearing in new accounts
    localStorage.removeItem('filtering');
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    
    // Set flag to prevent OAuth redirect from auto-logging in
    sessionStorage.setItem('justLoggedOut', 'true');
    
    // Clear any hash fragments that might contain OAuth tokens
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    
    setDropdownVisible(false);
    
    // Force redirect to login page with full page reload to clear all state
    window.location.href = '/login';
  };

  const onDarkModeToggle = () => {
    toggleDarkMode();
  };

  const onToggleLanguage = () => {
    setDropdownVisible(false);
    toggleLanguage();
  };

  React.useEffect(() => {
    // Function to calculate and set the top position of the dropdown container
    const setPosition = () => {
      const imagePlaceholder = document.getElementById('account_settings_circle');
      const dropdownContainer = document.getElementById('profile_dropdown_container');

      if (imagePlaceholder && dropdownContainer) {
        // Calculate the bottom position of the ImagePlaceHolder div
        const bottomPosition = imagePlaceholder.offsetTop + imagePlaceholder.offsetHeight + 15;

        // Set the top style value of the dropdown container
        dropdownContainer.style.top = `${bottomPosition}px`;
      }
    };

    // Call setPosition when isDropdownVisible changes
    setPosition();

    // Add an event listener for window resize (optional, to reposition on resize)
    window.addEventListener('resize', setPosition);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('resize', setPosition);
    };
  }, [isDropdownVisible]);

  return (
    <div ref={containerRef} className={styles.profile_picture_holder}>
      <div
        
        className={styles.image_wrapper}
        onClick={() => setDropdownVisible(!isDropdownVisible)}
        id="account_settings_circle"
        
      >
        <ImagePlaceHolder src={picture}  />
      </div>

      {isDropdownVisible && (
        <div
          className={styles.profile_dropdown_container}
          id="profile_dropdown_container"
          style={{
            top: 0,
            backgroundColor: theme.palette.mode === 'dark' ? '#2d2d2d' : 'white',
          }}
        >
          {/* Edit Profile */}
          <div className={styles.profile_dropdown_row} onClick={onEditProfile}>
            <div className={styles.arrow_up}>
              <div />
            </div>
            <span className={styles.profile_dropdown_text}>
              {t('nouns.settings')}
            </span>
          </div>

          {user && (user.isAdmin || user.accessLevel === 'LEAD') && (
            <div className={styles.profile_dropdown_row} onClick={onUsersClick}>
              <span className={styles.profile_dropdown_text}>
                {t('nouns.user_management')}
              </span>
            </div>
          )}

          {/* Billing Information */}
          <Flags authorizedFlags={[FeatureFlags.AllowBilling]}>
            {user && user.isAdmin && (
              <div className={styles.profile_dropdown_row} onClick={onBilling}>
                <span className={styles.profile_dropdown_text}>
                  {t('nouns.plans_and_payments')}
                </span>
              </div>
            )}
          </Flags>

          {/* Integrations */}
          <Flags authorizedFlags={[FeatureFlags.AllowIntegrations]}>
            <>
              {user && user.isAdmin && (
                <div
                  className={styles.profile_dropdown_row}
                  onClick={onIntegrations}
                >
                  <span className={styles.profile_dropdown_text}>
                    {t('nouns.integrations')}
                  </span>
                </div>
              )}
            </>
          </Flags>

          {/* Usage & credits — Account Owner / Admin only */}
          {canManageAccountSettings(user) && (
            <div className={styles.profile_dropdown_row} onClick={() => { navigate(PRIVATE.usage); setDropdownVisible(false); }}>
              <span className={styles.profile_dropdown_text}>
                {t('nouns.usage_and_credits')}
              </span>
            </div>
          )}

          {/* Switch language */}
          <div className={styles.profile_dropdown_row} onClick={onToggleLanguage}>
            <span className={styles.profile_dropdown_text}>
              {t('nouns.switch_language')}
            </span>
          </div>

          {/* Dark Mode Toggle */}
          <div className={styles.profile_dropdown_row} onClick={onDarkModeToggle}>
            <span className={styles.profile_dropdown_text}>
              {theme.palette.mode === 'dark' ? t('actions.switch_to_light_mode') : t('actions.switch_to_dark_mode')}
            </span>
          </div>

         

          {/* Log out */}
          <div className={styles.profile_dropdown_row} onClick={onLogout}>
            <span className={styles.profile_dropdown_text}>
              {t('verbs.logout')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const mapDispatchToProps = dispatch => ({
  clearState() {
    dispatch(clearAll());
  },
});

const ConnectedComponent = connect(
  null,
  mapDispatchToProps
)(ProfilePictureWithDropdown);

export default ConnectedComponent;
